import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '../styles/pages/user_analytics.css';
import '../styles/pages/company_drilldown.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { usePartnerRoles } from '@hooks/usePartnerRoles';
import { usePartnerOrganizationsQuery } from '@hooks/requests/queries/use_partner_organizations_query';
import { usePartnerOrganizationQuery } from '@hooks/requests/queries/use_partner_organization_query';
import { usePartnerCompaniesWithStatsQuery } from '@hooks/requests/queries/use_partner_companies_with_stats_query';
import { useAllUsersQuery } from '@hooks/requests/queries/use_all_users_query';
import {
  useBillingTrendQuery,
  scopeToSelectedPeriod,
} from '@hooks/requests/queries/use_billing_trend_query';
import { useMessagesTotalQuery } from '@hooks/requests/queries/use_messages_total_query';
import { DateRangeFilter, DropdownFilter } from '@ui';
import type { DateRangeValue } from '@ui';
import {
  AnalyticsSummaryWidgets,
  CompanyUserList,
  DashboardSkeleton,
} from '../components/dashboard';
import { getOrganizationName } from '../components/dashboard/organization_label';
import { isProvisionedByPeriodEnd } from '../components/dashboard/date_range_label';

const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const CompanyDrilldown: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // AceUp staff carry the org they were viewing through the URL — Partner
  // roles never need this, their org always comes from the JWT.
  const organizationIdFromQuery = searchParams.get('organizationId');

  const { isAceUpStaff, isPartnerRole } = usePartnerRoles();

  const [dateRange, setDateRange] = useState<DateRangeValue | null>(null);

  // Org list (AceUp staff — for the org name lookup) / own org (Partner roles).
  const { data: organizations } = usePartnerOrganizationsQuery(isAceUpStaff);
  const { data: ownOrganization } = usePartnerOrganizationQuery(isPartnerRole);

  const orgName = isAceUpStaff
    ? (() => {
        const org = organizations?.find((o) => o.organizationId === organizationIdFromQuery);
        return org ? getOrganizationName(org.organizationName, org.organizationId) : null;
      })()
    : ownOrganization?.organizationName?.trim() || null;

  // Full company roster for this org — used both for the breadcrumb
  // dropdown (alphabetical) and this company's own name/createdOn.
  const { data: companiesData, isLoading: companiesLoading } = usePartnerCompaniesWithStatsQuery({
    limit: 100,
    organizationId: isAceUpStaff ? organizationIdFromQuery : undefined,
    enabled: !isAceUpStaff || Boolean(organizationIdFromQuery),
  });

  const companies = companiesData?.companies ?? [];
  const currentCompany = companies.find((c) => c.companyId === companyId);

  const companyOptions = useMemo(
    () =>
      [...companies]
        .map((c) => ({ label: c.companyName, value: c.companyId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [companies]
  );

  const goToCompany = (nextCompanyId: string) => {
    const query = organizationIdFromQuery
      ? `?organizationId=${encodeURIComponent(organizationIdFromQuery)}`
      : '';
    navigate(`/companies/${encodeURIComponent(nextCompanyId)}${query}`);
  };

  // Users for this company — same client-side filter-by-company pattern as
  // the main User Analytics page (org scoping happens server-side).
  const { data: users, isLoading: isUsersLoading } = useAllUsersQuery({
    organizationId: isAceUpStaff ? organizationIdFromQuery : undefined,
    enabled: !isAceUpStaff || Boolean(organizationIdFromQuery),
  });

  const companyUsers = useMemo(
    () =>
      (users ?? []).filter(
        (u) => u.company_id === companyId && isProvisionedByPeriodEnd(u.created_at, dateRange)
      ),
    [users, companyId, dateRange]
  );

  const { trendData, isLoading: isTrendLoading } = useBillingTrendQuery(
    companyId ? [companyId] : [],
    dateRange,
    6,
    isAceUpStaff ? organizationIdFromQuery : undefined
  );

  // trendData may span a wider trailing window than the selected period
  // (for the chart's context, on pages that render one), so HWM narrows to
  // the exact selection before reducing. trendData is trusted as-is from
  // the backend (via aggregateTrendData's per-company carry-forward) — no
  // client-side flooring against the current user count.
  const hwmPoint = useMemo(
    () =>
      scopeToSelectedPeriod(trendData, dateRange).reduce<{ maxUserCount: number } | null>(
        (best, p) => (p.maxUserCount > (best?.maxUserCount ?? -1) ? p : best),
        null
      ),
    [trendData, dateRange]
  );

  const messageParams = useMemo(() => {
    if (!dateRange) return { companyId };
    return { from: toDateStr(dateRange.from), to: toDateStr(dateRange.to), companyId };
  }, [dateRange, companyId]);

  const { data: messagesData, isLoading: isMessagesLoading } = useMessagesTotalQuery(messageParams);

  if (companiesLoading || isUsersLoading || (isAceUpStaff && !organizationIdFromQuery)) {
    return <DashboardSkeleton />;
  }

  const companyName = currentCompany?.companyName ?? companyId ?? '';

  const createdOn = currentCompany?.createdOn
    ? new Date(currentCompany.createdOn).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const lastUpdated = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <div className="ua-page cd-page">
      <div className="cd-page-top">
        {/* Breadcrumb (prototype `.crumb`): back to User Analytics / company switcher */}
        <nav className="cd-crumb" aria-label="Breadcrumb">
          <button type="button" className="cd-crumb-back" onClick={() => navigate('/')}>
            <FontAwesomeIcon icon={faChevronLeft} className="cd-crumb-back-icon" />
            User Analytics
          </button>
          <span className="cd-crumb-sep" aria-hidden="true">
            /
          </span>
          <div className="cd-crumb-select">
            <DropdownFilter
              filterLabel={companyName}
              name="company-switcher"
              required
              options={companyOptions}
              value={companyId ?? null}
              onChange={(_name: string, value: string[] | string | null) => {
                const nextId = Array.isArray(value) ? (value[0] ?? null) : value;
                if (nextId) goToCompany(nextId);
              }}
            />
          </div>
        </nav>

        {/* Title + meta on one line (prototype `.page-head` / `.page-meta`) */}
        <div className="cd-page-head">
          <h1 className="cd-page-title">{companyName}</h1>
          <p className="cd-page-meta">
            {orgName && (
              <span className="cd-page-meta-item">
                Organization <span className="cd-page-meta-value">{orgName}</span>
              </span>
            )}
            {createdOn && (
              <span className="cd-page-meta-item">
                Created on <span className="cd-page-meta-value">{createdOn}</span>
              </span>
            )}
            <span className="cd-page-meta-item">
              <span className="cd-status-dot" aria-hidden="true" />
              Last updated <span className="cd-page-meta-value">{lastUpdated}</span>
            </span>
          </p>
        </div>
      </div>

      <div className="ua-stack">
        <div className="ua-filters">
          <div className="ua-field ua-field--date-range cd-field--date-range">
            <span className="ua-field-label">Date range</span>
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              minMonth={currentCompany?.createdOn ? new Date(currentCompany.createdOn) : null}
            />
          </div>
        </div>

        <AnalyticsSummaryWidgets
          totalUsers={companyUsers.length}
          hwm={hwmPoint?.maxUserCount ?? 0}
          totalMessages={messagesData?.totalMessages ?? null}
          isLoading={isTrendLoading || isMessagesLoading}
          dateRange={dateRange}
        />

        {companyId && (
          <CompanyUserList
            companyId={companyId}
            organizationId={isAceUpStaff ? organizationIdFromQuery : undefined}
            dateRange={dateRange}
          />
        )}
      </div>
    </div>
  );
};

export default CompanyDrilldown;
